import { useCallback, useState } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import { AgencyDashboardService } from '@/services/agencyDashboard.service';
import {
    buildTripsListWorkbook,
    tripsListFilename,
} from '@/lib/dashboard/trips/trips-list.export';
import type { AgencyTripsQuery } from '@/integrations/supabase/rpc-types';

interface ExportTripsListArgs {
    scope: 'all' | 'filtered';
    /** Filtri della vista; ignorati con scope 'all'. */
    query?: Omit<AgencyTripsQuery, 'limit' | 'offset'>;
    /** Nome del consulente filtrato, per il foglio "Contesto". */
    advisorName?: string | null;
}

/**
 * Scarica la lista viaggi come XLSX. La tab ha in mano una pagina sola, quindi le righe si
 * rileggono dal database senza paginazione: con i filtri correnti per la vista, senza
 * nessun filtro (periodo compreso) per "tutti i viaggi".
 */
export function useTripsListExport() {
    const [exporting, setExporting] = useState(false);
    const { t, language } = useI18n();

    const exportTrips = useCallback(
        async ({ scope, query, advisorName }: ExportTripsListArgs) => {
            setExporting(true);
            try {
                const effective: AgencyTripsQuery =
                    scope === 'all' || !query
                        ? { period: null, limit: null, offset: 0 }
                        : { ...query, limit: null, offset: 0 };

                const rows = await AgencyDashboardService.getTrips(effective);

                const blob = await buildTripsListWorkbook(rows, {
                    t,
                    scope,
                    period: effective.period,
                    stateFilter: effective.state ?? 'all',
                    search: effective.search,
                    advisorName,
                    locale: language === 'en' ? 'en-GB' : 'it-IT',
                });

                const filename = tripsListFilename(
                    scope === 'all'
                        ? t('dashboard.overview.trips.export.filenamePrefix')
                        : t('dashboard.overview.trips.export.filenamePrefixFiltered')
                );

                const url = URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = filename;
                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();
                // Rilasciato al giro dopo: revocarlo subito annulla il download su Safari.
                setTimeout(() => URL.revokeObjectURL(url), 0);

                toast({
                    title: t('dashboard.overview.trips.export.successTitle'),
                    description: filename,
                });
            } catch (error) {
                console.error('Trips list export failed:', error);
                toast({
                    title: t('common.error'),
                    description: t('dashboard.overview.trips.export.errorBody'),
                    variant: 'destructive',
                });
            } finally {
                setExporting(false);
            }
        },
        [t, language]
    );

    return { exporting, exportTrips };
}

export default useTripsListExport;
